// TODO: Add ellipsis property for long messages

import type { TExample } from '@atlas/uicorn/preview/modules';
import { useMemo } from 'react';
import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';
import useRefCallback from 'use-ref-callback';
import Tooltip, { type TTooltipProps } from '@atlas/uicorn/lib/Display/Tooltip';
import ChevronIcon from '@atlas/uicorn/lib/Icon/ChevronIcon';
import MoonIcon from '@atlas/uicorn/lib/Icon/MoonIcon';
import Spinner from '@atlas/uicorn/lib/Display/Spinner';
import window from '@atlas/tool/box/browser/window';
import type { TThemeColor } from '@atlas/uicorn/lib/System/StylesProvider';

const Option = React.memo(
    React.forwardRef<TOptionRef, TOptionProps>(function Option(props, ref): React.ReactElement {
        const {
            elementAs,
            type = 'button',
            noFocusStyle,
            noHoverStyle,
            noDisabledStyle,
            loading,
            ellipsis,
            danger = false,
            prefix,
            pushPrefix,
            suffix,
            pushSuffix,
            flex = 'start',
            children,
            tooltip,
            onClick,
            onMouseDown,
            propagate = false,
            delayClick = 0,
            href,
            hrefBlank,
            color,
            ...elementProps
        } = props;

        const handleClick = useRefCallback(function preventClickPropagation(event) {
            if (loading) return;

            // TODO: Maybe preventing default is good enough?
            if (!propagate) event.stopPropagation();
            try {
                onClick?.(event);
            } catch (error) {
                console.error(error);
            }
        });

        const handleClickCapture = useRefCallback<React.MouseEventHandler<HTMLButtonElement>>(
            function delayOrRejectEventIfNeeded(event) {
                const isDelayed = (event.nativeEvent as { __delayed?: boolean }).__delayed;

                if (isDelayed) return;

                if (delayClick < 0) {
                    // Event triggered by clipboard have this attributes equal to 0 and shouldn't be ignored
                    if (event.clientX || event.clientY) {
                        event.preventDefault();
                        event.stopPropagation();
                    }
                    return;
                }

                if (delayClick === 0) return;

                const { target } = event;
                if (!(target instanceof Node)) return;

                const clonedEvent = new MouseEvent('click', event.nativeEvent);
                Object.assign(clonedEvent, { __delayed: true });
                event.nativeEvent.stopPropagation();
                event.nativeEvent.preventDefault();

                setTimeout(() => {
                    if (window?.document.contains(target)) {
                        target.dispatchEvent(clonedEvent);
                    }
                }, delayClick);
            },
        );

        // TODO: With delayClick < 0 keydown with Enter and Space may also react faster
        const handleMouseDown = useRefCallback<React.MouseEventHandler<HTMLButtonElement>>(
            function mapEventToClickIfNeeded(event) {
                onMouseDown?.(event);
                if (event.defaultPrevented) return;

                if (delayClick >= 0) return;

                const { target } = event;
                if (!(target instanceof Node)) return;
                if (!window?.document.contains(target)) return;

                const clonedEvent = new MouseEvent('click', event.nativeEvent);
                Object.assign(clonedEvent, { __delayed: true });
                target.dispatchEvent(clonedEvent);
            },
        );

        const disabled = typeof props.disabled === 'boolean' ? props.disabled : !!props.loading;

        const tooltipProps = useMemo<undefined | Omit<TTooltipProps, 'children'>>(
            function normalizeTooltipProps() {
                if (typeof tooltip === 'undefined') return undefined;
                // const defaultProps = { placement: 'left' } as const;
                if (typeof tooltip === 'object') return tooltip;
                return { title: tooltip };
            },
            [tooltip],
        );

        // The `as` attribute should be used wisely
        // I mute it here not to have type conflicts with reference and event handlers types
        const transformingOptionAs = elementAs ?? href ? 'a' : undefined;
        const transformingOptions: any =
            transformingOptionAs === 'a'
                ? { as: transformingOptionAs, href, target: hrefBlank ? '_blank' : '_self' }
                : { as: transformingOptionAs };
        if (href && elementAs && elementAs !== 'a') {
            console.error('Buttons that are using href parameter should not use elementAs parameter');
        }

        return (
            <Tooltip {...tooltipProps}>
                <SOption
                    role="button"
                    tabIndex={0}
                    {...elementProps}
                    type={type}
                    disabled={disabled}
                    $flex={flex}
                    $focusStyle={!noFocusStyle}
                    $hoverStyle={!noHoverStyle}
                    onClick={handleClick}
                    onClickCapture={handleClickCapture}
                    onMouseDown={handleMouseDown}
                    $disabledStyle={!noDisabledStyle}
                    $color={color}
                    $danger={danger}
                    {...transformingOptions}
                    ref={ref}
                >
                    {(prefix || loading === 'prefix') && (
                        <SOptionAppendix $right={pushPrefix}>
                            {loading === 'prefix' ? <SSpinnerIcon $animate={!prefix} /> : prefix}
                        </SOptionAppendix>
                    )}
                    {children && ellipsis ? (
                        <SOptionChildren $ellipsis>{children}</SOptionChildren>
                    ) : children && typeof children === 'string' ? (
                        <SOptionChildren>{children}</SOptionChildren>
                    ) : (
                        children
                    )}
                    {(suffix || loading === 'suffix') && (
                        <SOptionAppendix $left={pushSuffix}>
                            {loading === 'suffix' ? <SSpinnerIcon $animate={!suffix} /> : suffix}
                        </SOptionAppendix>
                    )}
                </SOption>
            </Tooltip>
        );
    }),
);

export default Option;

// #region Styles

const spinnerIconAppearanceKeyframes = keyframes`
    to {
        margin-left: 0;
        opacity: 1;
    }
`;

const SSpinnerIcon = styled(Spinner)<{ $animate?: boolean }>`
    ${(p) =>
        p.$animate
            ? css`
                  margin-left: -2.2rem;
                  opacity: 0;
                  animation: ${spinnerIconAppearanceKeyframes} 0.2s ease-in-out forwards;
              `
            : ''}
`;

const SOptionChildren = styled.span<{ $ellipsis?: boolean }>`
    ${(p) => p.theme.overflowText()}
`;

export const SOptionAppendix = styled.span<{ $left?: boolean; $right?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: inherit;

    ${(p) =>
        p.$left
            ? css`
                  margin-left: auto;
              `
            : ''}
    ${(p) =>
        p.$right
            ? css`
                  margin-right: auto;
              `
            : ''}
`;

export const SOption = styled.button<{
    $flex: TOptionFlex;
    $focusStyle: boolean;
    $hoverStyle: boolean;
    $disabledStyle: boolean;
    $color?: TThemeColor;
    $danger: boolean;
}>`
    --button-transitions: scale 0.3s ease, outline-offset 0.1s ease, box-shadow 0.2s ease, filter 0.6s ease,
        background-color 0.3s ease, opacity 0.2s ease, color 0.3s ease;

    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    justify-content: ${(p) => (p.$flex === 'start' ? 'flex-start' : p.$flex === 'end' ? 'flex-end' : 'center')};
    text-align: ${(p) => (p.$flex === 'start' ? 'left' : p.$flex === 'end' ? 'right' : 'center')};

    border: none;
    opacity: 1;

    font: inherit;

    box-shadow: ${(p) => p.theme.shadow.no};

    transition: var(--button-transitions);
    will-change: scale;

    cursor: pointer;

    background-color: transparent;
    padding: 0;
    border-radius: ${(p) => p.theme.radius.small};

    &,
    &[disabled],
    &:hover {
        ${(p) => (p.$danger ? `color: ${p.theme.color.red6};` : p.$color ? `color: ${p.theme.color[p.$color]};` : '')}
    }

    // TODO: round them
    &:where(& > :first-child) {
        border-top-left-radius: inherit;
        border-bottom-left-radius: inherit;
    }
    &:where(& > :last-child) {
        border-top-right-radius: inherit;
        border-bottom-right-radius: inherit;
    }

    &:disabled,
    &[disabled] {
        cursor: inherit;
    }

    ${(p) =>
        p.$disabledStyle
            ? css`
                  &:disabled,
                  &[disabled] {
                      box-shadow: ${p.theme.shadow.no};

                      opacity: 0.2;
                      ${p.theme.when.dark(css`
                          opacity: 0.5;
                      `)}
                  }
              `
            : ''}

    &:focus-visible {
        outline: none;
    }

    ${(p) =>
        p.$focusStyle
            ? css`
                  &:focus-visible {
                      box-shadow: 0 0 0 0.1rem ${(p) => p.theme.color.primary};
                  }
              `
            : ''}

    ${(p) =>
        p.$hoverStyle
            ? css`
                  &:not(:disabled, [disabled]):hover {
                      background-color: ${(p) => p.theme.color.grey5};
                  }
              `
            : ''}
`;

// #endregion

// #region Types

export type TOptionFlex = 'center' | 'start' | 'end';

export type TOptionPropsTooltip = string | number | (() => React.ReactNode) | Omit<TTooltipProps, 'children'>;

export type TOptionProps = Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'prefix' | 'children' | 'onClickCapture'
> & {
    elementAs?: string;
    noFocusStyle?: boolean;
    noHoverStyle?: boolean;
    noDisabledStyle?: boolean;
    loading?: false | null | 'prefix' | 'suffix';
    danger?: boolean;
    prefix?: React.ReactNode;
    pushPrefix?: boolean;
    suffix?: React.ReactNode;
    pushSuffix?: boolean;
    flex?: TOptionFlex;
    propagate?: boolean;
    delayClick?: number;
    tooltip?: TOptionPropsTooltip;
    [dataAttribute: `data-${string}`]: string;
    href?: string; // Will use <a> tag unless other is not specified with [elementAs]
    hrefBlank?: boolean; // Will add target="_blank" for anchor tag
    color?: TThemeColor;
} & ({ ellipsis?: false; children?: React.ReactNode } | { ellipsis?: true; children?: string | number });

export type TOptionRef = HTMLButtonElement;

// #endregion

// #region Examples

export const Example: TExample = {
    containerStyle: {
        border: '1px dashed green',
    },
    render: ({ width, withTooltip, delayClick, link, ...props }) => {
        const style = width > 0 ? { width: `${width / 10}rem` } : undefined;

        const tooltip = withTooltip
            ? () => (
                  <span>
                      Refresh page <kbd>⌘R</kbd>
                  </span>
              )
            : undefined;

        return (
            <Option
                style={style}
                tooltip={tooltip}
                {...props}
                href={link ? '#' : undefined}
                delayClick={parseFloat(delayClick) || 0}
            />
        );
    },
    figma: [
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=4-1064&t=N252bJVpMS7uwjzX-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=21-1284&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=43-7700&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=52-5474&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=43-6969&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=28-6083&mode=design&t=TVklSB9u5fm4OQmw-0',
    ],
    controls: {
        children: { type: 'string', initial: 'Press me' },
        disabled: 'boolean',
        loading: ['none', 'prefix', 'suffix'],
        width: { type: 'number', initial: 200, connected: false },
        prefix: { type: 'string', initial: '🦄', connected: false },
        pushPrefix: 'boolean',
        suffix: { type: 'string', initial: '→', connected: false },
        pushSuffix: 'boolean',
        flex: ['center', 'start', 'end'],
        withTooltip: 'boolean',
        delayClick: ['-10', 'none (0)', '-1', '100'],
        link: 'boolean',
    },
    handlers: {
        onClick: 'console',
    },
};

export const AllVariantsExample: TExample = {
    layout: 'column',
    render(props) {
        const groupStyle: React.CSSProperties = { display: 'flex', gap: '0.5rem' };
        const wideStyle: React.CSSProperties = { width: '30rem' };
        return (
            <>
                <Option {...props}>Basic</Option>
                <Option {...props} href="#">
                    Basic link
                </Option>
                <Option {...props}>
                    Rich <strong>content</strong>
                </Option>
                <Option {...props} disabled>
                    Disabled
                </Option>
                <Option {...props} loading="prefix">
                    Loading=prefix
                </Option>
                <Option {...props} loading="prefix" disabled={false}>
                    Loading=prefix & not disabled
                </Option>
                <Option {...props} prefix={<MoonIcon />}>
                    Prefixed
                </Option>
                <Option {...props} suffix={<ChevronIcon />}>
                    Suffixed
                </Option>
                <Option {...props} prefix="🦄" suffix="→">
                    Prefixed & suffixed with strings
                </Option>
                <Option {...props} loading="prefix" prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Prefixed & suffixed & loading=prefix
                </Option>
                <Option {...props} loading="suffix" prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Prefixed & suffixed & loading=suffix
                </Option>
                <div style={groupStyle}>
                    <Option {...props} loading="prefix" />
                    <Option {...props} prefix={<MoonIcon />} />
                    <Option {...props} loading="prefix" suffix={<ChevronIcon />} />
                </div>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Long text that wraps when there is not enough space
                </Option>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />} ellipsis>
                    Option with the text that is cut with ellipsis
                </Option>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Prefixed & suffixed
                </Option>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} flex="start">
                    Wide & prefixed & flex start
                </Option>
                <Option {...props} style={wideStyle} suffix={<ChevronIcon />} flex="end">
                    Wide & suffixed & flex end
                </Option>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} pushPrefix suffix={<ChevronIcon />}>
                    Wide & pushed prefix & suffix
                </Option>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />} pushSuffix>
                    Wide & prefix & pushed suffix
                </Option>
                <Option
                    {...props}
                    style={wideStyle}
                    suffix={
                        <>
                            <MoonIcon />
                            <ChevronIcon />
                        </>
                    }
                    pushSuffix
                >
                    Two actions on the right
                </Option>
                <Option {...props} style={wideStyle} suffix={<ChevronIcon />} pushSuffix href="#">
                    Link with action on the right
                </Option>
            </>
        );
    },
    handlers: {
        onClick: 'console',
    },
    figma: [
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=4-1064&t=N252bJVpMS7uwjzX-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=21-1284&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=43-7700&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=52-5474&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=43-6969&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=28-6083&mode=design&t=TVklSB9u5fm4OQmw-0',
    ],
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Option', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Option>Press</Option>);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
